import './index.scss';
import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { reportError } from 'common/src/services/errorLogger';
import { Button, Modal, SnackBar, Tooltip, UmuSelect } from '@umu/umu-design';
import { ModalFuncProps } from '@umu/design-base';
import { _lang, isSystem, lang } from '@umu/ukit-lang';
import {
    IDataSourceProps,
    SOURCE_DATA_TYPE,
    IDataInfo,
    ISearchParams,
    ISourceInfo,
    ITabPane,
    IData,
} from '../../interface';
import { CustomTable, If, IfShow } from 'common/src/components';
import merge from 'lodash/merge';
import { FixedDirection } from 'common/src/components/customTable';
import { onEditorPermission } from '../../../permissionSettingModal/components/permissionContentInfo/dataFlow';

import {
    defaultList,
    defaultParams as defaultInitParams,
    DEFAULT_PAGE,
    tabParams,
    sourceImgTypeMap,
    getSessionList,
    getTreeData,
    knowledgeTYpeList,
} from '../../dataFlow';
import { columnsMap, getBlank, getColumns, TABLE_ID } from '../../dataFlow/columnsMap';
import { getFilterParams, getIdBuilder, showMsgToast } from '../../utils';
import { Tabs } from '../tabs';
import { PaginationV2 } from '../../../pagination';
import { GuidePopover } from '../../../guidePopover/guidePopover';
import { Input } from '../Input';
import { SelectTree } from '../selectTree';
import { UserSearchInput } from '../userSearchInput';
import { MEMBER_TYPE, MemberSearchSelect } from '../memberSearchSelect';
import { classSet } from '@umu/umu-dom';
import { onDialogAddMicroDialogPage } from '../../../microPage/components/addMicroDialogPage/index';
import { getCategoryList } from '../../server';
import isArray from 'lodash/isArray';
import { localStorageHelper } from '@umu/umu-storage';

const { confirm } = Modal;
enum CONTENT_TYPE {
    SEARCH_LIST,
    SELECT_LIST,
}
interface IDataPermissionSourceProps {
    list: IData | IData[];
    tabIndex: SOURCE_DATA_TYPE;
    suffixElement?: React.ReactNode | JSX.Element | string;
    onEditor?: () => void;
    onReset?: () => void;
    onClose?: () => void;
    onCheckPermissionValid?: (list: ISourceInfo[]) => Promise<JSX.Element | React.ReactNode | string>;
}
const DataPermissionSource = (props: IDataPermissionSourceProps) => {
    const { list, suffixElement = '', tabIndex, onReset, onClose, onCheckPermissionValid } = props;
    const dataList = isArray(list) ? list : [list];
    const columns = getColumns({ tabIndex, isAllowRadio: false, selectList: [], isHideOperate: true });
    const [validElement, setValidElement] = useState<JSX.Element | React.ReactNode | string>('');
    columns.push({
        name: lang('操作'),
        width: 180,
        dataIndex: 'operate',
        className: 'project-cell operate-cell',
        fixed: FixedDirection.right,
        render: (items: IData) => {
            return (
                <div className="rows-operate">
                    <Button
                        type="text"
                        isOnlyChildren
                        onClick={() => {
                            items?.id &&
                                onEditorPermission({
                                    related_content_id: items.id,
                                    related_type: tabIndex,
                                } as any);
                        }}
                    >
                        {lang('去编辑')}
                    </Button>
                    <Button
                        type="text"
                        isOnlyChildren
                        onClick={() => {
                            onReset?.();
                            onClose?.();
                        }}
                    >
                        {lang('重新选择')}
                    </Button>
                </div>
            );
        },
    });
    useEffect(() => {
        onCheckPermissionValid?.(dataList).then((res) => {
            setValidElement(res);
        });
    }, [dataList]);
    return (
        <div className="data-rows-source-panel">
            <CustomTable miniNumTdWidth={200} columns={columns} data={dataList}></CustomTable>
            <div>{suffixElement}</div>
            <div>{validElement}</div>
        </div>
    );
};

const DataSourceDialog = (props: IDataSourceProps) => {
    const {
        list = [],
        isHideTab = false,
        isHideToolbar = false,
        isAllowDialogCheck = false,
        isAllowRadio = false,
        isHideMarket = false,
        maxCount = 1,
        isAllowAddMicroPage = false,
        isHideSessionType = false,
        params = {},
        categoryList = [],
        suffixElement,
        isKnowledge = false,
        defaultTabIndex = SOURCE_DATA_TYPE.COURSE,
        tabList = tabParams,
        getSourceImg,
        onClose,
        onCheckPermissionValid,
    } = props;
    const [tabIndex, setTabIndex] = useState<SOURCE_DATA_TYPE>(defaultTabIndex);
    const [contentType, setContentType] = useState<CONTENT_TYPE>(CONTENT_TYPE.SEARCH_LIST);
    const [dataInfo, setDataInfo] = useState<IDataInfo>(defaultList);
    const defaultParams: ISearchParams = merge({}, defaultInitParams, params);
    const [searchParams, setSearchParams] = useState<ISearchParams>(defaultParams);
    const [isLoad, setIsLoad] = useState(true);
    const [isClear, setIsClear] = useState(false);
    const [selectList, setSelectList] = useState<ISourceInfo[]>(list);
    const isAdmin = !!props?.userInfo?.enterprise_info && String(props?.userInfo?.enterprise_info?.role_type) === '4';
    const cacheKey = `market_entry_micro_page_${props?.userInfo?.umu_id}`;
    const [showMarketGuide, setShowMarketGuide] = useState(false);
    const guideRoot = useRef<HTMLElement | undefined>();
    const showMarketEntry = isAdmin && (isSystem('CN') || isSystem('JP'));

    const marektEntryRoot = useCallback((node: any) => {
        if (node) {
            guideRoot.current = node;
            setShowMarketGuide(showMarketEntry && localStorageHelper.get(cacheKey) !== '1');
        }
    }, []);
    const updateSearchParams = (val: string, keys: string) => {
        const newSearchPArams = { ...searchParams };
        newSearchPArams[keys] = val;
        setSearchParams(newSearchPArams);
    };
    const onCompleteClick = () => {
        props.onSourceChange(selectList.slice(0, maxCount));
        onClose?.();
    };
    const onSearch = async (params: ISearchParams, sourceType?: SOURCE_DATA_TYPE) => {
        const configObj: any = columnsMap[sourceType || tabIndex];
        const res = !!configObj.getDataFunc ? await configObj.getDataFunc(getFilterParams(params)) : defaultList;
        setIsLoad(false);
        setDataInfo(res);
        return !!res;
    };
    const onValidChange = (_item: ITabPane) => {
        return !isLoad;
    };
    const onPageChange = async (page: number) => {
        setIsLoad(true);
        const newParams: any = {
            page,
            size: 20,
            is_filter_by_enterprise_role: '0',
            ...columnsMap[tabIndex].searchParam,
            ...searchParams,
        };
        if (!isKnowledge && newParams?.is_course_in_lib === 1) {
            delete newParams.is_course_in_lib;
        }
        onSearch(newParams);
    };
    const onAddMicroPage = () => {
        const newProps = props;
        onClose?.();
        onDialogAddMicroDialogPage({
            onSuccess: (data) => {
                const { isSuccess = false, info } = data || {};
                if (isSuccess) {
                    props.onSourceChange([
                        {
                            imgSrc: '',
                            title: info?.title || '',
                            link: info?.share_url || '',
                            option: {
                                type: SOURCE_DATA_TYPE.MICRO_PAGES,
                                contentId: getIdBuilder(),
                                id: info?.id || '',
                                data: info,
                            },
                            sourceImgType: 0,
                        },
                    ]);
                } else {
                    SnackBar.show({
                        message: lang('新建微页面失败'),
                    });
                    onDataSourceDialog(newProps);
                }
            },
        });
    };
    const onSelectSource = (items: IData, isRadio: boolean, isDel = false) => {
        if (isDel) {
            setSelectList(selectList.filter((item) => item.option?.id !== items.id));
            return;
        }
        const newSelectList = isRadio ? [] : [...selectList];
        if (newSelectList.length >= maxCount && maxCount > 1) {
            showMsgToast(lang('最多添加{$0{0=个,1=个}}内容', maxCount));
            return;
        }
        const isReplaceIndex = maxCount === 1;
        const params = (getSourceImg && getSourceImg(tabIndex, items)) || {
            title: '',
            imgSrc: '',
            link: '',
        };
        const newItem = {
            sourceImgType: sourceImgTypeMap[tabIndex],
            ...params,
            option: {
                type: tabIndex,
                contentId: getIdBuilder(),
                id: tabIndex === SOURCE_DATA_TYPE.LECTURER ? items.umu_id : items.id,
                data: items,
            },
        };
        if (isReplaceIndex) {
            newSelectList[0] = newItem;
        } else {
            newSelectList.push(newItem);
        }
        setSelectList(newSelectList);
        if (isAllowDialogCheck) {
            setContentType(CONTENT_TYPE.SELECT_LIST);
        }
    };

    const onTabChange = async (item: ITabPane) => {
        if (isLoad) {
            return false;
        }
        setIsLoad(true);
        const newTabIndex = item.key as SOURCE_DATA_TYPE;
        setSearchParams(defaultParams);
        setTabIndex(newTabIndex);
        const isResult = await onSearch(
            {
                page: DEFAULT_PAGE,
                size: 20,
                is_filter_by_enterprise_role: '0',
                ...columnsMap[newTabIndex].searchParam,
                ...defaultParams,
            },
            newTabIndex,
        );
        return isResult;
    };
    useEffect(() => {
        onPageChange(DEFAULT_PAGE);
    }, []);
    const marketGuideData = {
        text: lang('“课程市场”入口仅系统管理员可见。'),
        btnText: lang('我知道了'),
        callback() {
            try {
                localStorageHelper.set(cacheKey, '1');
                setShowMarketGuide(false);
            } catch (err) {
                reportError({
                    error: err,
                    componentName: 'DataSourceDialog',
                    additionalInfo: { desc: 'save market guide state failed', cacheKey },
                });
            }
        },
    };
    const renderTab = () => {
        return (
            <Tabs
                params={tabList}
                isValidChange
                activeKey={tabIndex}
                onChange={onTabChange}
                onValidChange={onValidChange}
            ></Tabs>
        );
    };
    const renderPagination = () => {
        const total = dataInfo.page_info.list_total_num;
        return (
            <div className="panel-footer">
                {total > 0 && <div className="total-static">{lang('共 $0 条').replace('$0', String(total))}</div>}
                {dataInfo.page_info.total_page_num > 1 ? (
                    <PaginationV2
                        currentPage={dataInfo.page_info.current_page}
                        maxPage={dataInfo.page_info.total_page_num || 1}
                        showSizeChanger
                        onPageChange={onPageChange}
                    />
                ) : null}
            </div>
        );
    };
    const renderToolbarAct = () => {
        return (
            <div className="tool-bar-btn">
                <Button
                    type="ghost-blue"
                    className="tool-bar-btn-split"
                    onClick={() => {
                        onPageChange(DEFAULT_PAGE);
                    }}
                >
                    {lang('搜索')}
                </Button>
                <Button
                    type="ghost-blue"
                    onClick={() => {
                        setIsLoad(true);
                        setSearchParams(defaultParams);
                        onSearch({
                            page: DEFAULT_PAGE,
                            size: 20,
                            is_filter_by_enterprise_role: '0',
                            ...columnsMap[tabIndex].searchParam,
                            ...defaultParams,
                        });
                        setIsClear(!isClear);
                    }}
                >
                    {lang('清空')}
                </Button>
            </div>
        );
    };

    const renderToolbar = () => {
        switch (tabIndex) {
            case SOURCE_DATA_TYPE.COURSE:
                return (
                    <div className="form-rows">
                        <div className="params-split">
                            <SelectTree
                                className="source-select-tree"
                                treeData={categoryList}
                                selectedKeys={
                                    searchParams.category_id ? String(searchParams.category_id).split(',') : []
                                }
                                placeholder={lang('所有课程分类')}
                                onChange={(keys: { toString: () => string }) =>
                                    updateSearchParams(keys.toString(), 'category_id')
                                }
                            />
                        </div>
                        <div className="params-split">
                            <Tooltip
                                placement="bottom"
                                hideArrow
                                mouseEnterDelay={0.5}
                                getTooltipContainer={() => document.getElementById(TABLE_ID) || document.body}
                                title={!searchParams.group_title ? lang('课程名称（或访问码，或描述，或标签）') : ''}
                            >
                                <div>
                                    <Input
                                        id="choose-course-name-input"
                                        option={{ placeholder: lang('课程名称（或访问码，或描述，或标签）_short') }}
                                        className="search-keyword"
                                        defaultValue={String(searchParams.group_title)}
                                        onChange={(val: string) => updateSearchParams(val, 'group_title')}
                                    />
                                </div>
                            </Tooltip>
                        </div>
                        <div className="params-split">
                            <Tooltip
                                placement="bottom"
                                hideArrow
                                mouseEnterDelay={0.5}
                                getTooltipContainer={() => document.getElementById(TABLE_ID) || document.body}
                                title={
                                    !searchParams.group_title ? lang('创建者姓名（或邮箱，或手机号，或用户名）') : ''
                                }
                            >
                                <UserSearchInput
                                    id="choose-creator-name-input"
                                    onChange={(res) => updateSearchParams(res.toString(), 'uids')}
                                    limitMax={1}
                                    isClear={isClear}
                                    placeholder={lang('创建者姓名（或邮箱，或手机号，或用户名）_short')}
                                />
                            </Tooltip>
                        </div>
                        <div className="params-split empty-right">{renderToolbarAct()}</div>
                    </div>
                );
            case SOURCE_DATA_TYPE.LECTURER:
                return (
                    <div className="form-rows form-rows-program">
                        <div className="params-split">
                            <MemberSearchSelect
                                sourceType={MEMBER_TYPE.GROUP}
                                onSelect={(key) => updateSearchParams(key, 'enterprise_group_ids')}
                                defaultValue={String(searchParams.enterprise_group_ids)}
                            />
                        </div>
                        <div className="params-split">
                            <MemberSearchSelect
                                sourceType={MEMBER_TYPE.DEPARTMENT}
                                onSelect={(key) => updateSearchParams(key, 'department_ids')}
                                defaultValue={String(searchParams.department_ids)}
                            />
                        </div>
                        <div className="params-split">
                            <UserSearchInput
                                onChange={(res) => updateSearchParams(res.toString(), 'uids')}
                                limitMax={1}
                                isClear={isClear}
                                placeholder={lang('创建者姓名（或邮箱，或手机号，或用户名）')}
                            />
                        </div>
                        <div className="params-split empty-right">{renderToolbarAct()}</div>
                    </div>
                );
            case SOURCE_DATA_TYPE.MICRO_PAGES:
                return (
                    <Fragment>
                        <If condition={isAllowAddMicroPage}>
                            <Button type="ghost-blue" className="form-rows-split" onClick={onAddMicroPage}>
                                {lang('新建微页面')}
                            </Button>
                        </If>
                        <div className="form-rows form-rows-program">
                            <div className="params-split">
                                <Tooltip
                                    placement="bottom"
                                    hideArrow
                                    mouseEnterDelay={0.5}
                                    title={!searchParams.keyword ? lang('页面名称（或备注）') : ''}
                                    getTooltipContainer={() => document.getElementById(TABLE_ID) || document.body}
                                >
                                    <div>
                                        <Input
                                            option={{ placeholder: lang('页面名称（或备注）') }}
                                            className="search-program"
                                            defaultValue={String(searchParams.keyword)}
                                            onChange={(val: string) => updateSearchParams(val, 'keyword')}
                                        />
                                    </div>
                                </Tooltip>
                            </div>
                            <div className="params-split">
                                <UserSearchInput
                                    onChange={(res) => updateSearchParams(res.toString(), 'uids')}
                                    limitMax={1}
                                    isClear={isClear}
                                    placeholder={lang('创建者姓名（或邮箱，或手机号，或用户名）')}
                                />
                            </div>
                            <div className="params-split empty-right">{renderToolbarAct()}</div>
                        </div>
                    </Fragment>
                );
            case SOURCE_DATA_TYPE.PROGRAM:
                return (
                    <div className="form-rows form-rows-program">
                        <div className="params-split">
                            <Tooltip
                                placement="bottom"
                                hideArrow
                                mouseEnterDelay={0.5}
                                getTooltipContainer={() => document.getElementById(TABLE_ID) || document.body}
                                title={
                                    !searchParams.program_title ? lang('学习项目名称（或访问码，或描述，或标签）') : ''
                                }
                            >
                                <div>
                                    <Input
                                        option={{ placeholder: lang('学习项目名称（或访问码，或描述，或标签）') }}
                                        className="search-program"
                                        defaultValue={String(searchParams.program_title)}
                                        onChange={(val: string) => updateSearchParams(val, 'program_title')}
                                    />
                                </div>
                            </Tooltip>
                        </div>
                        <div className="params-split">
                            <UserSearchInput
                                onChange={(res) => updateSearchParams(res.toString(), 'uids')}
                                limitMax={1}
                                isClear={isClear}
                                placeholder={lang('创建者姓名（或邮箱，或手机号，或用户名）')}
                            />
                        </div>
                        <div className="params-split empty-right">{renderToolbarAct()}</div>
                    </div>
                );
            case SOURCE_DATA_TYPE.SESSION:
                return (
                    <div>
                        <div className="form-rows">
                            <div className="params-split">
                                <SelectTree
                                    className="source-select-tree"
                                    treeData={categoryList}
                                    selectedKeys={
                                        searchParams.category_id ? String(searchParams.category_id).split(',') : []
                                    }
                                    placeholder={lang('所有课程分类')}
                                    onChange={(keys: { toString: () => string }) =>
                                        updateSearchParams(keys.toString(), 'category_id')
                                    }
                                />
                            </div>
                            <If condition={!isHideSessionType}>
                                <div className="params-split">
                                    <UmuSelect
                                        width="2.18rem"
                                        options={getSessionList(
                                            [9],
                                            [
                                                {
                                                    value: '',
                                                    label: lang('所有小节类型'),
                                                },
                                            ],
                                        )}
                                        value={String(searchParams.session_type)}
                                        onChange={(keys) => {
                                            updateSearchParams(keys as string, 'session_type');
                                        }}
                                    />
                                </div>
                            </If>
                            <div className="params-split">
                                <UserSearchInput
                                    onChange={(res) => updateSearchParams(res.toString(), 'uids')}
                                    limitMax={1}
                                    isClear={isClear}
                                    placeholder={lang('创建者姓名（或邮箱，或手机号，或用户名）')}
                                />
                            </div>
                        </div>
                        <div className="form-rows form-line">
                            <div className="params-split">
                                <Tooltip
                                    placement="bottom"
                                    hideArrow
                                    title={
                                        !searchParams.group_title ? lang('课程名称（或访问码，或描述，或标签）') : ''
                                    }
                                    mouseEnterDelay={0.5}
                                    getTooltipContainer={() => document.getElementById(TABLE_ID) || document.body}
                                >
                                    <div>
                                        <Input
                                            option={{ placeholder: lang('课程名称（或访问码，或描述，或标签）') }}
                                            className="search-keyword"
                                            defaultValue={String(searchParams.group_title)}
                                            onChange={(val: string) => updateSearchParams(val, 'group_title')}
                                        />
                                    </div>
                                </Tooltip>
                            </div>
                            <div className="params-split">
                                <Tooltip
                                    placement="bottom"
                                    hideArrow
                                    mouseEnterDelay={0.5}
                                    title={!searchParams.session_title ? lang('小节名称或小节标签') : ''}
                                    getTooltipContainer={() =>
                                        document.querySelector('search-keyword') || document.body
                                    }
                                >
                                    <div>
                                        <Input
                                            option={{ placeholder: lang('小节名称或小节标签') }}
                                            className="search-keyword"
                                            defaultValue={String(searchParams.session_title)}
                                            onChange={(val: string) => updateSearchParams(val, 'session_title')}
                                        />
                                    </div>
                                </Tooltip>
                            </div>
                            <div className="params-split empty-right">{renderToolbarAct()}</div>
                        </div>
                    </div>
                );
            case SOURCE_DATA_TYPE.PRACTICE_CHNNEL:
                return (
                    <div>
                        <div className="form-rows form-line">
                            <div className="params-split">
                                <Tooltip
                                    placement="bottom"
                                    title={!searchParams.keyword && !_lang('cn') ? lang('搜索练习频道名称或描述') : ''}
                                    mouseEnterDelay={0.5}
                                    getTooltipContainer={() => document.getElementById(TABLE_ID) || document.body}
                                >
                                    <div>
                                        <Input
                                            option={{ placeholder: lang('搜索练习频道名称或描述') }}
                                            className="search-keyword"
                                            defaultValue={String(searchParams.keyword)}
                                            onChange={(val: string) => updateSearchParams(val, 'keyword')}
                                        />
                                    </div>
                                </Tooltip>
                            </div>
                            <div className="params-split">
                                <div className="tool-bar-btn">
                                    <Button
                                        type="ghost-blue"
                                        className="tool-bar-btn-split"
                                        onClick={() => {
                                            onPageChange(DEFAULT_PAGE);
                                        }}
                                    >
                                        {lang('搜索')}
                                    </Button>
                                </div>
                            </div>
                            <div className="params-split empty-right">
                                <div className="select-msg">
                                    {lang('最多选{$0}个，已选{$1}个', maxCount, selectList.length)}
                                </div>
                            </div>
                        </div>
                    </div>
                );
        }
        return '';
    };
    const showKnowledgeType = isKnowledge && knowledgeTYpeList.includes(tabIndex);
    return (
        <div
            className={classSet({
                'data-source-panel': true,
                [`data-source-panel_${tabIndex}`]: true,
                'data-source-loading-or-empty': isLoad || dataInfo.page_info.list_total_num === 0,
            })}
            id={TABLE_ID}
        >
            <IfShow show={contentType === CONTENT_TYPE.SEARCH_LIST}>
                <If condition={!isHideTab}>{renderTab()}</If>
                <If condition={!isHideToolbar}>
                    <div className="toolbar-zone">{renderToolbar()}</div>
                </If>
                <CustomTable
                    columns={getColumns({ tabIndex, isAllowRadio, selectList, maxCount, onSelectSource })}
                    data={dataInfo.list}
                    blank={getBlank(dataInfo.page_info.list_total_num, isLoad, showKnowledgeType, {
                        isPracticeChannel: tabIndex === SOURCE_DATA_TYPE.PRACTICE_CHNNEL,
                    })}
                ></CustomTable>
                {suffixElement}
            </IfShow>
            <If condition={contentType === CONTENT_TYPE.SELECT_LIST}>
                <Fragment>
                    <DataPermissionSource
                        list={selectList.map((item) => item?.option?.data)}
                        tabIndex={tabIndex}
                        onCheckPermissionValid={async (_list) => {
                            return await onCheckPermissionValid?.(selectList);
                        }}
                        onReset={() => setContentType(CONTENT_TYPE.SEARCH_LIST)}
                    />
                    <div className="dialog-panel-footer flex-right">
                        <Button type="primary-blue" onClick={onCompleteClick}>
                            {props.completeText || lang('完成')}
                        </Button>
                    </div>
                </Fragment>
            </If>
            <IfShow show={contentType === CONTENT_TYPE.SEARCH_LIST}>
                <div className="dialog-panel-footer">
                    {renderPagination()}
                    <div className="dialog-panel-footer-operate">
                        <div className="marekt-entry" ref={marektEntryRoot}>
                            <If condition={!!showMarketEntry && !isHideMarket}>
                                <>
                                    <If condition={showMarketGuide}>
                                        <GuidePopover
                                            require
                                            root={guideRoot.current}
                                            placement="topLeft"
                                            guideData={marketGuideData}
                                            trigger="active"
                                        />
                                    </If>
                                    <a href="/market" target="_blank">
                                        {lang('去课程市场选择课程')}
                                    </a>
                                </>
                            </If>
                        </div>
                        <Button type="primary-blue" onClick={onCompleteClick}>
                            {props.completeText || lang('完成')}
                        </Button>
                    </div>
                </div>
            </IfShow>
        </div>
    );
};

/**
 * @desc 微页面配置信息弹窗页面 打开弹窗函数
 */
const onDataSourceDialog = async (data: IDataSourceProps, dialogInfo?: ModalFuncProps) => {
    const { title = lang('选择内容'), className = '', zIndex = 1000, ...rest } = dialogInfo || {};
    const newData = { ...data };
    const { list = [], maxCount } = data;
    if (list.length > maxCount) {
        SnackBar.show({
            message: lang('最多只能添加{$0{0=个,1=个}}', maxCount),
        });
        return;
    }
    if (newData?.isReloadCategory) {
        const treeList = await getCategoryList({ is_with_course_num: 0 });
        const categoryList = getTreeData(treeList);
        newData.categoryList = categoryList;
    }

    const dialog = confirm({
        title,
        centered: true,
        width: '8.6rem',
        footer: null,
        zIndex,
        className: `global-source-data-page-modal ${className}`,
        ...rest,
        content: (
            <DataSourceDialog
                {...newData}
                onClose={() => {
                    data?.onClose?.();
                    dialog.destroy();
                }}
            />
        ),
    } as any);
    return {
        dialog,
        categoryList: newData.categoryList,
    };
};

const onDialogDataPermissionSource = (data: IDataPermissionSourceProps) => {
    const onRefresh = () => {
        dialog.update({
            content: <DataPermissionSource {...data} />,
        });
    };
    const dialog = confirm({
        title: (
            <div>
                {lang('关联内容')}
                <Button type="text" className="btn-split" isOnlyChildren onClick={onRefresh}>
                    {lang('刷新')}
                </Button>
            </div>
        ),
        centered: true,
        width: '8.6rem',
        okText: lang('完成'),
        zIndex: 1000,
        okType: 'primary-blue',
        className: 'global-source-permission-data-page-modal',
        content: (
            <DataPermissionSource
                {...data}
                onClose={() => {
                    dialog.destroy();
                }}
            />
        ),
    });
    return dialog;
};
export { DataSourceDialog, onDataSourceDialog, onDialogDataPermissionSource };
